import { authorizedFetch } from '../../helpers/authorizedFetch'
import trans from '../../helpers/trans'
import { generateApiV3Url } from '../../helpers/url'
import { FetchCollectionResult, FetchResult, FetchResultType } from '../FetchResult'
import {
    AddChangeLogEntryFormData,
    ChangeLogEntry,
    ChangeLogEntryResource,
    EditChangeLogEntryFormData,
} from './ChangeLog'
import {
    transformAddChangeLogEntryFormDataToAddApiParams,
    transformEditChangeLogEntryFormDataToEditApiParams,
    transformToChangeLogEntry,
} from './ChangeLogTransformers'

export const getChangeLogApiCall = async (): Promise<FetchCollectionResult<ChangeLogEntry[], string>> => {
    try {
        const url = generateApiV3Url({ endpoint: '/change-log-entries' })

        const response = await authorizedFetch(url)

        const doc: ChangeLogEntryResource[] = await response.json()

        if (! Array.isArray(doc)) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            amountOfPages: 1,
            status: response.status,
            type: FetchResultType.Success,
            data: doc.map(transformToChangeLogEntry),
        }
    } catch (error) {
        console.error('[getChangeLogApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const postChangeLogEntryApiCall = async (
    addChangeLogEntryFormData: AddChangeLogEntryFormData,
): Promise<FetchResult<ChangeLogEntry, string>> => {
    const apiData = transformAddChangeLogEntryFormDataToAddApiParams(addChangeLogEntryFormData)

    try {
        const url = generateApiV3Url({ endpoint: '/change-log-entries' })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify(apiData),
        })

        const doc: ChangeLogEntryResource = await response.json()

        if (! doc) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: 200,
            type: FetchResultType.Success,
            data: transformToChangeLogEntry(doc),
        }
    } catch (error) {
        console.error('[postChangeLogEntryApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const patchChangeLogEntryApiCall = async (
    editChangeLogEntryFormData: EditChangeLogEntryFormData,
): Promise<FetchResult<ChangeLogEntry, string>> => {
    const apiData = transformEditChangeLogEntryFormDataToEditApiParams(editChangeLogEntryFormData)

    try {
        const url = generateApiV3Url({ endpoint: `/change-log-entries/${apiData.id}` })

        const response = await authorizedFetch(url, {
            method: 'PATCH',
            body: JSON.stringify(apiData),
        })

        const doc: ChangeLogEntryResource = await response.json()

        if (! doc) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: 200,
            type: FetchResultType.Success,
            data: transformToChangeLogEntry(doc),
        }
    } catch (error) {
        console.error('[patchChangeLogEntryApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const deleteChangeLogEntryApiCall = async (
    changeLogEntryId: string,
): Promise<FetchResult<undefined, string>> => {
    try {
        const url = generateApiV3Url({ endpoint: `/change-log-entries/${changeLogEntryId}` })

        const response = await authorizedFetch(url, { method: 'DELETE' })

        if (! response.ok) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        return {
            status: 200,
            type: FetchResultType.Success,
            data: undefined,
        }
    } catch (error) {
        console.error('[deleteChangeLogEntryApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
